import "@tento-ui/component-builtin";
import "@tento-ui/components/calendar/calendar-column.mock";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Center } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { MockCalendarRow } from "@tento-ui/components/calendar/calendar-column.mock";
import { DarkMode } from "@tento-ui/dark-mode";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ThemesClient } from "@tento-lona";
import { Actor } from "@tento-lona/sheets";
import { ThemePickerContent } from "../../../theme-picker";
import { reportWriteError } from "../../../app/errors";

@component({ name: "std-nux-theme-card" })
export class NuxThemeCard extends LonaWebComponent {
  private $calendar: MockCalendarRow = this.hydrate("calendar");
  private $themes: HTMLElement = this.$("themes");

  private themes: Option<ThemesClient> = null;

  static makeWith(themes: ThemesClient): NuxThemeCard {
    return NuxThemeCard.make().bind(themes);
  }

  bind(themes: ThemesClient): this {
    this.themes = themes;
    const currentTheme = this.themes!.live.get() as Option<ThemesClient.Theme>;
    this.rebindCalendar(currentTheme);

    for (const info of this.themes!.builtin.all()) {
      const $item = Center.make();
      $item.classList.add("theme-item");
      if (currentTheme?.name === info.id) {
        $item.toggleAttribute("selected", true);
      }

      const $gradient = document.createElement("div");
      $gradient.classList.add("theme-gradient");
      $gradient.assignStyles({ background: info.preview });
      $item.appendChild($gradient);

      GESTURE_MANAGER.addPointerEvent($item, {
        onClick: async () => {
          const theme = await this.themes!.get(info.id);
          if (theme) {
            // Shows at once; a refused write takes it back and is reported.
            void this.themes!.setDurable(theme, Actor.EXTERNAL).settled.catch((e: unknown) => reportWriteError("change the theme", e));
            this.rebindCalendar(theme);
            this.$themes
              .querySelector("[selected]")
              ?.toggleAttribute("selected", false);
            $item.toggleAttribute("selected", true);
          }
        },
      });
      this.$themes.appendChild($item);
    }

    return this;
  }

  private rebindCalendar(theme: Option<ThemesClient.Theme>) {
    this.$calendar.bind(ThemePickerContent.makeEvents(theme));
    this.$calendar.assignStyles({
      "--sheet-background-color": theme?.semanticColor(
        "sheet",
        "background",
        DarkMode.isDarkMode() ? "dark" : "light",
      ),
    });
  }

  static $styles = [
    css`
      :host {
        display: block;
        height: 100%;
      }

      #container {
        height: 100%;
        overflow: hidden;
      }

      #calendar-anchor {
        position: relative;
        flex-grow: 1;
        overflow: hidden;
        border-radius: 12px;
        margin: 8px;
      }

      #calendar {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
      }

      #themes {
        gap: 8px;
        padding-inline: 12px;
        margin-top: 8px;
        margin-bottom: 16px;
        justify-content: center;
      }

      .theme-item {
        position: relative;
        width: 20px;
        height: 20px;
        cursor: pointer;
      }

      .theme-item[selected]::before {
        z-index: 0;
        content: "";
        position: absolute;
        inset: -2px;
        background: var(--fg-icon-sec);
        border-radius: 10px;
      }

      .theme-gradient {
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        border-radius: 8px;
      }
    `,
  ];

  static $html = html`
    <std-col id="container">
      <div id="calendar-anchor">
        <std-mock-calendar-row id="calendar"></std-mock-calendar-row>
      </div>
      <std-row id="themes"></std-row>
    </std-col>
  `;
}
